import { getTheme, Image, ImageFit, SharedColors, Stack, Text } from '@fluentui/react';
import { CurrencyCodes, CurrencySigns, ICampaign, IGradeCategoryGrade, IOfferProviderConfig, IPreOfferItem, IPreOfferSummary } from '@piceasoft/core';
import * as React from 'react';
import { getIconSymbolsFromString, getPersonaColorsPallete } from '../../../../../core/scripts/style';
import { CustomSeparator } from '../../../../../components/shared/decorations/CustomSeparator';
import { strings } from '../../../../../localization/strings';
import { PromotionPriceTypes } from '@piceasoft/core';
import { CommissionType } from '@piceasoft/core';

/** Offer provider price item */
export type TProviderPriceWithPromo = {
    price: number
    priceCurrencySign?: string
    grade?: string
    gradeCategories?: IGradeCategoryGrade[]
    promo?: {
        price: number
        currencySign?: string
        priceType: PromotionPriceTypes
    }
    priceWithPromo?: number
    commissionValue?: number
    commissionType?: CommissionType
}

type TPreOfferPivotProps = {
    summary: IPreOfferSummary
    offerProviders?: IOfferProviderConfig[]
}

const getPriceWithCommission = (price: number = 0, commissionValue?: number, commissionType?: CommissionType) => {
    if (!commissionValue || !commissionType) {
        return price
    }
    let result = commissionType === CommissionType.Percentage ? price - (price * commissionValue) / 100 : price - commissionValue;
    if(result < 0)
        result = 0

    return Number(result.toFixed(2));
}

const priceWithPromo = ( promotion: ICampaign, offer: IPreOfferItem) : number | undefined => {
    if (promotion.price_type === PromotionPriceTypes.Benefit) {
        return getPriceWithCommission(offer.price, offer.commission_value, offer.commission_type) + (promotion.price ?? 0)
    }
    if (promotion.price_type === PromotionPriceTypes.FixPrice) {
        return promotion.price
    }
    return undefined;
}

export const PreOfferPivot: React.FC<TPreOfferPivotProps> = (props) => {
    const items = props.summary.offers.map( (offer, index) => {
        const d = props.offerProviders?.find( p => p.code === offer.provider_code);
        const cc = offer.currency ? CurrencySigns[offer.currency as CurrencyCodes] : ''
        const pcc = offer.campaign?.currency ? CurrencySigns[offer.campaign?.currency as CurrencyCodes] : cc
        const price = getPriceWithCommission( offer.price, offer.commission_value, offer.commission_type);
        const providerPrice = {
            price: price ?? 0,
            priceCurrencySign: cc,
            grade: offer.grade,
            commissionValue: offer.commission_value,
            commissionType: offer.commission_type,
            promo: !offer.campaign ? undefined : {
                price: offer.campaign?.price,
                currencySign: pcc,
                priceType: offer.campaign?.price_type
            }
        } as TProviderPriceWithPromo
        if( offer.campaign)
            providerPrice.priceWithPromo = priceWithPromo( offer.campaign, offer)
        const name = offer.provider_name ?? d?.name ?? '';

        return (<ProviderBlock 
                    name={name} 
                    description={d?.description} 
                    imageSrc={d?.imageSrc} 
                    providerPrice={providerPrice} 
                    key={name+index}/>)
    })

    return (
        <Stack grow tokens={{ childrenGap: 16,}} >
            <Stack grow horizontalAlign="center" tokens={{ childrenGap: 8 }} >
                {items && items.length > 0 && (<>
                        <Text variant="large" style={{ color: SharedColors.gray30, paddingTop:'36px' }}>{strings.CONSTRUCTOR.STAGES.COMMON_OFFER.PROVIDER_FORM.TITLE}</Text>
                        {items}
                    </>) || 
                    (<Text variant="medium" style={{ color: SharedColors.gray30 }}>{strings.PROCESS.OFFER.NO_OFFERS}</Text> )
                }
            </Stack>
        </Stack>
    )
}

type TProviderBlockProps = {
    name: string
    description?: string
    imageSrc?: string
    providerPrice: TProviderPriceWithPromo
}

const ProviderBlock: React.FC<TProviderBlockProps> = (props) => {
    const maxPrice = props.providerPrice;
    return (
        <Stack grow style={{ maxWidth: 800, width: '100%' }}>
            <Stack verticalFill horizontal grow verticalAlign="center" tokens={{ childrenGap: 10, padding: "8px 8px 0px 8px" }}>
                <Stack verticalAlign='center' horizontal grow tokens={{ childrenGap: 16 }}>
                    {props.imageSrc && (
                        <Stack style={{ borderRadius: 4, backgroundColor: theme.palette.white, boxShadow: theme.effects.elevation16, padding: 2 }}>
                            <Image src={props.imageSrc} width={38} height={38} imageFit={ImageFit.cover} />
                        </Stack>
                    ) || (
                            <Stack
                                verticalAlign="center"
                                horizontalAlign="center"
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 4,
                                    backgroundColor: getPersonaColorsPallete[props.name[0].toLowerCase()],
                                    boxShadow: theme.effects.elevation16
                                }}>
                                <Text variant="large" style={{ color: "#fff", fontSize: 16, fontWeight: 100, paddingBottom: 2 }}>
                                    {getIconSymbolsFromString(props.name)}
                                </Text>
                            </Stack>
                        )
                    }
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="medium" style={{ fontWeight: "bold", color: SharedColors.gray30 }}>{props.name}</Text>
                        <Text variant="small" style={{ color: SharedColors.gray10 }}>{props.description}</Text>
                    </Stack>
                </Stack>
                <Stack.Item verticalFill>
                    <Stack verticalFill verticalAlign="end">
                        <PriceWithPromo item={maxPrice} hideTotal />
                    </Stack>
                </Stack.Item>
            </Stack>
            <CustomSeparator />
        </Stack >
    )
}

type TPriceWithPromoProps = {
    item?: TProviderPriceWithPromo
    bold?: boolean
    hideTotal?: boolean
}
const PriceWithPromo: React.FC<TPriceWithPromoProps> = ({item, bold, hideTotal}) => {

    if (!item || !item.price) {
        return (<Stack>
            {strings.PROCESS.OFFER.NO_OFFERS}
        </Stack>)
    }

    const textThickness = bold ? 600 : undefined
    const priceSize = ( !hideTotal && item?.priceWithPromo) ? "medium" : "large"

    return (
        item.promo && (
            item.promo.priceType === PromotionPriceTypes.Benefit && (
                <Stack horizontalAlign='end'>
                    { (!hideTotal && item?.priceWithPromo) && (
                        <Text style={{ color: SharedColors.green10, fontWeight: textThickness }} variant="large">{(item.priceWithPromo)?.toLocaleString()} <small>{item.promo.currencySign}</small></Text>    
                    ) || ( <>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Text style={{ color: SharedColors.orangeYellow20, fontWeight:  textThickness }} variant={priceSize}>{(item.price).toLocaleString()} <small>{item.priceCurrencySign}</small></Text>
                    </Stack>
                    <Text style={{ color: SharedColors.green10, fontWeight:  textThickness }}>+{((item.promo.price)).toLocaleString()} <small>{item.promo.currencySign}</small></Text>
                    </>
                    )}
                </Stack>
            ) || ( //promo fixed price
                <Stack horizontalAlign='end' tokens={{ childrenGap: 2 }}>
                    {item.priceWithPromo && (<Text style={{ color: SharedColors.gray20, textDecoration: "line-through", fontWeight:  textThickness }} variant="smallPlus">{item.price.toLocaleString()} <small>{item.priceCurrencySign}</small></Text>)}
                    <Text style={{ color: item?.priceWithPromo ? SharedColors.green10 : SharedColors.orangeYellow20, fontWeight:  textThickness }} variant="large">{(item?.priceWithPromo ?? item?.price)?.toLocaleString()} <small>{item.promo.currencySign}</small></Text>
                </Stack>
            )
        ) || ( //only price, no promo
            <Text style={{ color: SharedColors.orangeYellow20, fontWeight:  textThickness }} variant="large">{item.price?.toLocaleString()} <small>{item.priceCurrencySign}</small></Text>
        )
    )
}



const theme = getTheme();

